import {Button, DatePicker, Empty, message, Select, Spin, Typography} from 'antd';
import React, { useEffect, useState } from 'react'
import moment, { Moment } from 'moment';
import { DT0Format, DateFormat } from '../../../../../utils/StaticVariable';
import { ChartBox } from '../../../ResultPanel/component/ChartBox';
import { IGetRosePlotInsituPayload } from '../../../../../types/chart';
import { IEnrichedMetadata } from '../../../../../types/metadata';
import { IBestDataset, IItemsValidation } from '../../../../../types/analytics';
import { MarkerItem } from '../../../../../types/home';
import { DownloadCheckboxKey } from '../../../../../utils/common';
import RoseImage from '../../../../../assets/image/K13aCMEMS-Rose.png'
import Api from '../../../../../api/Api';
import { LoadingOutlined } from '@ant-design/icons';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { AnalyticsSection } from "../../../Analytics/components/Layout/AnalyticsSection";

const { Text } = Typography;

interface IRosePlot {
  dateRange?: [moment.Moment, moment.Moment]
  validInSituPoint?: boolean
  activePoint?: MarkerItem
  activeTab: number
  enrichedMetadata?: IEnrichedMetadata[]
  insituName?: string
  listParameters: IItemsValidation[],
  activeCategoryKey: string
  setActiveCategory: Function
  userGroupPermissions: UserGroupPermissions;
  datasetId: string
  bestDataset?: IBestDataset[]
}

const degreeIntervals = [4, 8, 12, 16, 32, 36];

const RosePlot: React.FC<IRosePlot> = ({
  activeCategoryKey,
  setActiveCategory,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  insituName,
  listParameters,
  dateRange,
  activeTab,
  userGroupPermissions,
  datasetId,
  bestDataset
}) => {
  const [variable, setVariable] = useState('')
  const [numberOfDirection, setNumberOfDirection] = useState(0)
  const [rosePlotImages, setRosePlotImages] = useState('')
  const [_, setLinks] = useState([])
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (enrichedMetadata && listParameters.length > 0 && userGroupPermissions && validInSituPoint && dateRange) {
      const parameter = listParameters[0].name
      const direction = 16
      setVariable(parameter)
      setNumberOfDirection(16)
      setStartDate(moment(dateRange[0]))
      setEndDate(moment(dateRange[1]))
      const values = {
        datasetName: datasetId,
        numberOfDirection: direction,
        parameters: parameter,
        startDate: startDate?.format(DT0Format) ?? moment(dateRange[0]).format(DT0Format),
        endDate: endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if(activeTab === 2 && rosePlotImages === ''){
        onLoadRosePlot(values, enrichedMetadata)
      }
    }

  }, [activeTab, enrichedMetadata, listParameters, userGroupPermissions, activePoint?.coordinates, validInSituPoint, dateRange])

  const onLoadRosePlot = async (values: IGetRosePlotInsituPayload, enrichedMetadata: IEnrichedMetadata[]) => {
    setLoading(true)
    setRosePlotImages('')
    setLinks([])
    try {
      const category = bestDataset?.find(
        (bd: IBestDataset) => bd.id === values.datasetName
      );
      if (category !== undefined) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === values.datasetName &&
            task.CoordinatesString === activePoint?.coordinates.join()
        );
        if(task){
          const res = await Api.chart.getRosePlotInsitu(
            insituName ?? '',
            task.OrderId,
            values.numberOfDirection,
            values.parameters,
            values.startDate,
            values.endDate,
          )
          if(res?.img){
            setRosePlotImages('data:image/png;base64,' + res.img)
          }
          if(res?.links){
            setLinks(res.links)
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }

  }

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onVariableChange = (value: string) => {
    setVariable(value)
  }

  const onNoOfDirectionChange = (value: number) => {
    setNumberOfDirection(value)
  }

  const onChangeParameter = (value: string) => {
    setActiveCategory(value)
  }

  const handleUpdate = () => {
    if (enrichedMetadata && datasetId && startDate && endDate) {
      const values = {
        datasetName: datasetId,
        parameters: variable,
        numberOfDirection: numberOfDirection,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadRosePlot(values, enrichedMetadata)
    }
  }

  const showUpdateFormMessage = () => {
    message.warn('Please click Update once you finished your selection')
  }

  return (
    <AnalyticsSection
      plotInputContent={
        <div style={{display: 'flex', gap: '8px'}}>
          <div>
            <Text>Parameters</Text>
            <Select
              value={activeCategoryKey ? activeCategoryKey as string : DownloadCheckboxKey.SW as string}
              onBlur={showUpdateFormMessage}
              onChange={onChangeParameter}
              style={{width: '100%', marginTop: '4px', minWidth: 150}}
              disabled={!validInSituPoint || loading}
            >
              {Object.entries(DownloadCheckboxKey).map(([key, val]) => (
                <Select.Option key={key} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Text>Variables</Text>
            <Select
              value={variable}
              onBlur={showUpdateFormMessage}
              onChange={onVariableChange}
              disabled={!validInSituPoint || loading}
              style={{width: '100%', marginTop: '4px', minWidth: 150}}
            >
              {listParameters
                .map((item) => {
                  return (
                    <Select.Option key={item.id} value={item.name}>
                      {item.name}
                    </Select.Option>
                  );
                })}
            </Select>
          </div>

          <div>
            <Text>No. of Direction</Text>
            <Select
              value={numberOfDirection}
              onBlur={showUpdateFormMessage}
              onChange={onNoOfDirectionChange}
              disabled={!validInSituPoint || loading}
              style={{width: '100%', marginTop: '4px', minWidth: 150}}
            >
              {degreeIntervals.map((interval) => {
                return (
                  <Select.Option key={interval} value={interval}>
                    {interval}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div>
            <Text>Start Date</Text>
            <DatePicker
              value={moment(startDate)}
              onBlur={showUpdateFormMessage}
              onChange={onChangeStartDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
              style={{width: '100%', marginTop: '4px'}}
            />
          </div>

          <div>
            <Text>End Date</Text>
            <DatePicker
              value={moment(endDate)}
              onBlur={showUpdateFormMessage}
              onChange={onChangeEndDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
              style={{width: '100%', marginTop: '4px'}}
            />
          </div>
        </div>
      }
      updateContent={
        <Button
          type='primary'
          onClick={handleUpdate}
          disabled={!validInSituPoint || loading}
          style={{width: 100, bottom: 10}}
        >
          Update
        </Button>
      }
      isLoading={loading}
      chartContent={
        <ChartBox
          key={'Rose Plot'}
          title={'Rose Plot'}
          height={700}
          plot={
            <>
              {loading && <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin/>} />}
              {!loading && rosePlotImages.trim() !== '' && (
                <img
                  style={{
                    width: '1200px',
                    objectFit: 'scale-down',
                    maxHeight: '65vh',
                    marginTop: '50px'
                  }}
                  src={RoseImage}
                  alt="RosePlot"
                />
              )}
              {!loading && rosePlotImages === '' && (
                <Empty />
              )}
            </>
          }
          dataset=''
        />
      }
    />
  )
}

export default RosePlot
